import React, { useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import colors from "assets/theme-dark/base/colors";
import CustomerSegmentationCard from "./components/segmentation/SegmentationCard";
import AnalysisDriverCard from "./components/drivers/AnalysisDriverCard";
import HeadroomHeader from "./components/HeadRoomHeader";
import MDBox from "../../../components/MDBox";
import SegmentationTable from "./components/tables/SegmentationTable";
import DriverMetricsTable from "./components/tables/DriverMetricsTable";
import { useHeadroomData } from "./hooks/useHeadroomData";
import CollapseButton from "./components/CollapseButton";
import TabSelector from "./components/TabSelector";
import { Stack } from "@mui/material";
import { useHeadroomContext } from "./context/HeadRoomProvider";
import { Dashboard } from "./components/Dashboard";
import HeadroomFinalTable from "./components/tables/HeadroomFinalResultsTable";

export interface Column {
  name: string;
  isNumeric: boolean;
  min?: number;
  max?: number;
}

const Headroom: React.FC = () => {
  const { data, loading, processedData, filteredOptions, setSearchTerm, handleFetchMore, columns } = useHeadroomData();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [isCardsCollapsed, setIsCardsCollapsed] = useState<boolean>(true);

  const columnNames = columns?.map((column: Column) => column.name) || [];

  const toggleCardsCollapse = (): void => {
    setIsCardsCollapsed(!isCardsCollapsed);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {
    setActiveTab(newValue);
  };

  const { selectedProject } = useHeadroomContext();

  const initialProject = useRef<string>(null);
  initialProject.current = selectedProject;

  return (
    <StyledContainer component={motion.div}>
      <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", flex: 1 }}>
        <HeadroomHeader
          data={data}
          loading={loading}
          filteredOptions={filteredOptions}
          setSearchTerm={setSearchTerm}
          handleFetchMore={handleFetchMore}
        />
        <MDBox sx={{ display: "flex", gap: 1.5, width: "100%", position: "relative" }}>
          <CollapseButton isCollapsed={isCardsCollapsed} onToggle={toggleCardsCollapse} />
          <MDBox sx={{ width: "50%" }}>
            <CustomerSegmentationCard
              processedData={processedData?.processedDataTableHeaders}
              segmentationTypes={columnNames}
              isCollapsed={isCardsCollapsed}
              setIsCollapsed={setIsCardsCollapsed}
            />
          </MDBox>
          <MDBox sx={{ width: "50%" }}>
            <AnalysisDriverCard columns={columns} isCollapsed={isCardsCollapsed} />
          </MDBox>
        </MDBox>
        <Stack style={{ marginTop: isCardsCollapsed ? "-10px" : "10px", height: "100%", flex: 1 }}>
          <TabSelector isCollapsed={isCardsCollapsed} onTabChange={handleTabChange} activeTab={activeTab} />
          {activeTab === 0 && <SegmentationTable columns={columns} />}
          {activeTab === 1 && <DriverMetricsTable columns={columns} />}
          {activeTab === 2 && <HeadroomFinalTable />}
          {activeTab === 3 && <Dashboard selectedProjectName={selectedProject} />}
        </Stack>
      </div>
    </StyledContainer>
  );
};

const StyledContainer = styled(MDBox)(({ theme }) => ({
  padding: "2px",
  background: colors.background.default,
  minHeight: "100%",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
}));

export default Headroom;
